import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/modules/auth/hooks/useAuth';
import { WebsiteActivitySection } from '@/modules/website-analytics/components/WebsiteActivitySection';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Icons } from '@/shared/components/ui/icons';
import { getVisibleNavItems } from '@/shared/layout/nav-items';
import { cn } from '@/shared/lib/cn';

export const Route = createFileRoute('/dashboard/')({
  component: DashboardIndexPage,
});

function DashboardIndexPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSuperAdmin = user?.roles.includes('super_admin') ?? false;
  const modules = getVisibleNavItems(isSuperAdmin).filter((item) => !item.exact);

  return (
    <div className="mx-auto max-w-7xl space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('dashboard.title')}</h1>
        <p className="mt-1 text-muted-foreground">{t('dashboard.welcome')}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('dashboard.profile.email')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="truncate font-mono text-sm">{user?.email}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('dashboard.profile.roles')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1">
              {user?.roles.map((role) => (
                <span
                  key={role}
                  className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                >
                  {role}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('dashboard.profile.status')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <span className="inline-flex items-center gap-1.5 text-sm">
              <span
                className={cn(
                  'h-2 w-2 rounded-full',
                  user?.isActive ? 'bg-success' : 'bg-muted-foreground',
                )}
              />
              {user?.isActive ? t('dashboard.profile.active') : t('dashboard.profile.inactive')}
            </span>
          </CardContent>
        </Card>
      </div>

      <WebsiteActivitySection />

      <div>
        <h2 className="text-lg font-semibold text-foreground">{t('dashboard.modulesHeading')}</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {modules.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className="group rounded-xl border border-border bg-card p-5 shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary/15">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground group-hover:text-primary">
                      {t(item.labelKey)}
                    </p>
                    {item.descriptionKey ? (
                      <CardDescription className="mt-1 line-clamp-2">
                        {t(item.descriptionKey)}
                      </CardDescription>
                    ) : null}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
